import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  FileCheck, 
  Search, 
  Bell, 
  Award, 
  Users, 
  TrendingUp,
  CheckCircle,
  Clock,
  Target,
  Zap,
  Lock,
  Globe,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  X
} from 'lucide-react';
import ContactPopup from '../components/ContactPopup';
import PricingSection from '../components/PricingSection';
import FAQSection from '../components/FAQSection';
import IndustriesSection from '../components/IndustriesSection';
import { Helmet } from 'react-helmet';

const HomePage = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      const scrollPosition = window.scrollY + 100;

      sections.forEach(section => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;
        const sectionId = section.getAttribute('id');

        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          setActiveSection(sectionId);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const services = [
    {
      icon: <Search className="w-12 h-12" />,
      title: "Comprehensive Trademark Search",
      description: "Our expert legal team conducts thorough, multi-level searches across USPTO databases, state registrations, common law sources, and domain name registries to identify potential conflicts before filing. We analyze phonetic similarities, visual appearances, conceptual meanings, and translation equivalents across multiple languages. Our detailed search reports include risk assessments, likelihood of approval analysis, and strategic recommendations to strengthen your application. This proactive approach saves time and money by identifying issues early, allowing you to make informed decisions about your brand protection strategy.",
      image: "https://images.unsplash.com/photo-1619418602850-35ad20aa1700"
    },
    {
      icon: <FileCheck className="w-12 h-12" />,
      title: "USPTO Filing & Registration",
      description: "Navigate the complex USPTO filing process with confidence through our experienced attorneys who understand every nuance of trademark law. We handle complete application preparation, selecting precise international classification codes, drafting comprehensive descriptions of goods and services, gathering required specimens of use, and preparing compelling statements of use. Our meticulous attention to detail minimizes office actions and expedites the registration process. We file electronically for faster processing and provide you with immediate confirmation and serial number assignment.",
      image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85"
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: "Brand Protection Strategy",
      description: "Develop comprehensive, multi-layered strategies to safeguard your intellectual property across classes, jurisdictions, and platforms. Our approach includes trademark portfolio analysis, defensive registration strategies, monitoring for marketplace infringement, enforcement recommendations, and domain name protection. We help you build a robust trademark portfolio that grows with your business, covering product lines, geographic expansion, and brand extensions. Our proactive protection prevents costly disputes and ensures your brand remains exclusively yours in an increasingly competitive marketplace.",
      image: "https://images.unsplash.com/photo-1468779036391-52341f60b55d"
    },
    {
      icon: <Bell className="w-12 h-12" />,
      title: "Trademark Monitoring & Enforcement",
      description: "Stay ahead of potential threats with our continuous trademark monitoring services that track new USPTO applications, state registrations, domain name registrations, and marketplace activity across e-commerce platforms and social media. We provide regular reports highlighting potential conflicts and unauthorized uses. When infringement is detected, we assist with enforcement strategies including cease and desist letters, opposition proceedings, and litigation support. Early detection and swift action protect your brand's integrity and market position while minimizing enforcement costs.",
      image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85"
    }
  ];

  const benefits = [
    {
      icon: <Lock className="w-10 h-10" />,
      title: "Exclusive Nationwide Rights",
      description: "Federal trademark registration grants you exclusive nationwide rights to use your mark in connection with your goods or services, regardless of where you actually do business. This protection creates a legal barrier preventing competitors from using identical or confusingly similar marks anywhere in the United States. You gain the legal presumption of ownership, the right to use the prestigious ® symbol, and priority over later users. These exclusive rights significantly strengthen your market position, deter potential infringers, and provide a solid foundation for brand expansion across state lines and into new markets."
    },
    {
      icon: <Shield className="w-10 h-10" />,
      title: "Comprehensive Legal Protection",
      description: "Federal registration provides powerful legal protections that far exceed common law rights. You receive legal presumption of ownership and validity, meaning in disputes, the burden shifts to others to prove otherwise. Your registration grants standing to bring infringement suits in federal court, access to enhanced damages and attorney fee awards, and the ability to prevent imports of infringing goods through US Customs. The USPTO listing creates public notice of your claim, deterring potential infringers. You can also leverage your federal registration to stop cybersquatters and recover infringing domain names through UDRP proceedings."
    },
    {
      icon: <TrendingUp className="w-10 h-10" />,
      title: "Increased Brand Value & Business Growth",
      description: "A registered trademark significantly increases your company's valuation by creating quantifiable intellectual property assets that appear on your balance sheet. It enhances credibility and professionalism with customers, investors, lenders, and business partners who recognize the ® symbol as a mark of legitimacy and commitment. Your trademark becomes licensable, creating additional revenue streams through licensing agreements and franchise opportunities. It facilitates mergers and acquisitions by providing clear ownership of brand assets. Registered trademarks also improve investor confidence and can increase company valuations during funding rounds or exit events."
    },
    {
      icon: <Globe className="w-10 h-10" />,
      title: "International Expansion Foundation",
      description: "Your US federal registration serves as the foundation for international trademark protection through various treaties and agreements. It enables you to file international applications through the Madrid Protocol, covering over 120 countries with a single application. You can claim priority based on your US filing date when seeking protection abroad, potentially blocking later foreign applications. The registration demonstrates your serious commercial intent to international trademark offices. For businesses planning global expansion, your US registration is often required by foreign jurisdictions and simplifies the process of securing trademark rights in key international markets."
    }
  ];

  const whyChooseUs = [
    {
      icon: <Award className="w-12 h-12" />,
      title: "USPTO Filing Experts",
      stat: "1000+",
      description: "Successfully Filed Trademarks",
      detail: "Over 1,000 trademark registrations across diverse industries with specialized experience in trademark law."
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: "Dedicated Support",
      stat: "24/7",
      description: "Client Support Available",
      detail: "Personalized attention from dedicated case managers with prompt responses throughout your trademark journey."
    },
    {
      icon: <Target className="w-12 h-12" />,
      title: "High Success Rate",
      stat: "95%",
      description: "Approval Rate",
      detail: "Industry-leading approval rate through meticulous searches, application preparation, and expert office action responses."
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: "Fast Processing",
      stat: "Same Day",
      description: "Application Filing",
      detail: "Streamlined process enables same-day filing for most applications, getting your trademark into USPTO quickly."
    }
  ];

  const process = [
    {
      step: "01",
      title: "Comprehensive Search & Consultation",
      description: "Begin with a free consultation where we understand your brand and business goals. Our expert team conducts an extensive search of federal and state databases, common law sources, and internet presence to identify potential conflicts. You receive a detailed report with our professional analysis, honest feedback on registrability, and strategic recommendations for your trademark application."
    },
    {
      step: "02",
      title: "Application Preparation & USPTO Filing",
      description: "Our experienced attorneys carefully prepare your trademark application, selecting appropriate international classes, crafting precise descriptions of goods and services, and gathering all required specimens and documentation. We electronically file your complete application with the USPTO and provide you with the filing receipt and serial number. Your trademark enters the federal registration process with all information properly submitted for examination."
    },
    {
      step: "03",
      title: "Examination, Registration & Ongoing Protection",
      description: "We monitor the USPTO examination process and promptly respond to any office actions or objections from the examining attorney. Once approved, your trademark is published for opposition and proceeds to final registration. You receive your official registration certificate and can begin using the ® symbol. We provide ongoing support for maintenance filings, renewals, and enforcement to protect your valuable brand assets for years to come."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Professional Trademark Registration Services | Trademark Organization Name</title>
        <meta name="description" content="Protect your brand with expert USPTO trademark registration services. 1000+ trademarks filed, 95% success rate. Fast, affordable trademark protection. Same-day filing available." />
        <meta name="keywords" content="trademark registration, USPTO filing, brand protection, trademark search, trademark attorney, business trademark" />
      </Helmet>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-2">
              <Shield className="w-8 h-8 text-secondary" />
              <span className="text-xl font-bold text-primary">Trademark Organization Name</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('about')} className="nav-link">About</button>
              <button onClick={() => scrollToSection('services')} className="nav-link">Services</button>
              <button onClick={() => scrollToSection('process')} className="nav-link">Process</button>
              <button onClick={() => scrollToSection('pricing')} className="nav-link">Pricing</button>
              <button onClick={() => scrollToSection('faq')} className="nav-link">FAQ</button>
              <Button onClick={() => setIsPopupOpen(true)} className="btn-primary">
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="pt-32 pb-20 bg-gradient-to-br from-primary via-primary to-primary/90">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Protect Your Brand with Expert Trademark Registration
              </h1>
              <p className="text-xl mb-8 text-white/90 leading-relaxed">
                Secure your brand identity with professional USPTO trademark registration services. 
                Over 1,000 trademarks successfully filed. Fast, reliable, and affordable protection 
                for your business name, logo, and brand assets.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button onClick={() => setIsPopupOpen(true)} size="lg" className="btn-secondary">
                  Register My Trademark <ChevronRight className="ml-2" />
                </Button>
                <Button onClick={() => scrollToSection('contact')} size="lg" variant="outline" className="btn-outline-light">
                  Free Consultation
                </Button>
              </div>
              <div className="mt-12 grid grid-cols-3 gap-6">
                <div>
                  <div className="text-3xl font-bold text-secondary">1000+</div>
                  <div className="text-sm text-white/80">Trademarks Filed</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-secondary">95%</div>
                  <div className="text-sm text-white/80">Success Rate</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-secondary">24/7</div>
                  <div className="text-sm text-white/80">Support</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d" 
                alt="Professional trademark registration services"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-secondary text-primary p-6 rounded-xl shadow-xl">
                <div className="text-2xl font-bold">Same Day Filing</div>
                <div className="text-sm">Get started today</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-padding">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="section-title">About Trademark Organization Name</h2>
            <div className="w-24 h-1 bg-secondary mx-auto mb-8"></div>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Leading provider of professional trademark registration services with over a decade of experience. 
              We've helped thousands protect their brand assets through strategic USPTO registration.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Located in Los Angeles, we serve clients nationwide with accessible, affordable trademark services 
              backed by experienced attorneys and personalized support.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="card-hover p-8 text-center">
              <Award className="w-16 h-16 text-secondary mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-primary mb-3">Expert Team</h3>
              <p className="text-gray-600">
                Our attorneys specialize exclusively in trademark law with years of USPTO filing experience
              </p>
            </Card>
            <Card className="card-hover p-8 text-center">
              <Shield className="w-16 h-16 text-secondary mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-primary mb-3">Proven Results</h3>
              <p className="text-gray-600">
                95% success rate with over 1,000 trademarks successfully registered and protected
              </p>
            </Card>
            <Card className="card-hover p-8 text-center">
              <Users className="w-16 h-16 text-secondary mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-primary mb-3">Client-Focused</h3>
              <p className="text-gray-600">
                Dedicated case managers provide personalized support throughout your trademark journey
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Trademark Overview */}
      <section className="section-padding bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="section-title text-center">What is Trademark Registration?</h2>
            <div className="w-24 h-1 bg-secondary mx-auto mb-12"></div>
            
            <div className="space-y-8">
              <div className="bg-white p-8 rounded-xl shadow-sm">
                <h3 className="text-2xl font-bold text-primary mb-4">Understanding Trademarks</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  A trademark is a distinctive identifier—word, phrase, design, symbol, or combination—that distinguishes 
                  your goods or services from competitors. It's the legal foundation of your brand identity, representing 
                  everything your business stands for: quality, reputation, and customer trust. Whether it's the Nike swoosh, 
                  Apple's bitten apple, or McDonald's golden arches, trademarks create instant brand recognition and 
                  consumer loyalty.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Federal USPTO registration provides nationwide protection, creating exclusive rights to use your mark 
                  in commerce. This protection extends beyond common law rights, offering legal presumption of ownership, 
                  public notice of your claim, and the coveted ® symbol that signals federal registration to competitors 
                  and customers alike.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-6 bg-white">
                  <h4 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
                    <CheckCircle className="w-6 h-6 text-secondary" />
                    What Can Be Trademarked?
                  </h4>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-secondary mt-1 flex-shrink-0" />
                      <div>
                        <strong>Business Names & Trade Names:</strong> Your company name and the names under which 
                        you operate, protecting your corporate identity in the marketplace.
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-secondary mt-1 flex-shrink-0" />
                      <div>
                        <strong>Logos, Symbols & Designs:</strong> Visual representations including company logos, 
                        graphic designs, and stylized text that create brand recognition.
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-secondary mt-1 flex-shrink-0" />
                      <div>
                        <strong>Slogans & Taglines:</strong> Memorable phrases like "Just Do It" or "I'm Lovin' It" 
                        that become synonymous with your brand.
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-secondary mt-1 flex-shrink-0" />
                      <div>
                        <strong>Product Names & Packaging:</strong> Names of specific products or services and 
                        distinctive packaging designs that identify your offerings.
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-secondary mt-1 flex-shrink-0" />
                      <div>
                        <strong>Sounds & Colors:</strong> In specific contexts, distinctive sounds (like NBC's 
                        chimes) or colors (like Tiffany blue) can receive trademark protection.
                      </div>
                    </li>
                  </ul>
                </Card>

                <Card className="p-6 bg-white">
                  <h4 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
                    <Clock className="w-6 h-6 text-secondary" />
                    Registration Timeline
                  </h4>
                  <div className="space-y-4 text-gray-700">
                    <div className="flex items-start gap-3">
                      <div className="bg-secondary/10 rounded-full p-2 flex-shrink-0">
                        <span className="font-bold text-secondary">1-2</span>
                      </div>
                      <div>
                        <strong className="text-primary">Days: Application Filed</strong>
                        <p className="text-sm mt-1">Your trademark application is prepared and electronically 
                        filed with the USPTO, receiving a serial number.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-secondary/10 rounded-full p-2 flex-shrink-0">
                        <span className="font-bold text-secondary">3-4</span>
                      </div>
                      <div>
                        <strong className="text-primary">Months: Initial Examination</strong>
                        <p className="text-sm mt-1">USPTO examining attorney reviews your application for 
                        compliance with trademark law and potential conflicts.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-secondary/10 rounded-full p-2 flex-shrink-0">
                        <span className="font-bold text-secondary">6-8</span>
                      </div>
                      <div>
                        <strong className="text-primary">Months: Publication Period</strong>
                        <p className="text-sm mt-1">If approved, your mark is published in the Official Gazette 
                        for 30-day opposition period.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-secondary/10 rounded-full p-2 flex-shrink-0">
                        <span className="font-bold text-secondary">10-12</span>
                      </div>
                      <div>
                        <strong className="text-primary">Months: Registration Issued</strong>
                        <p className="text-sm mt-1">Final registration certificate issued, granting full federal 
                        protection and the right to use ®.</p>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="bg-primary text-white p-8 rounded-xl">
                <h3 className="text-2xl font-bold mb-4">Why Federal Registration Matters</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold mb-3 text-secondary">Legal Advantages</h4>
                    <ul className="space-y-2 text-white/90">
                      <li className="flex items-start gap-2">
                        <ChevronRight className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                        <span>Nationwide exclusive rights regardless of where you do business</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                        <span>Legal presumption of ownership and validity in court</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                        <span>Ability to file trademark infringement suits in federal court</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                        <span>Basis for international trademark registration</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold mb-3 text-secondary">Business Benefits</h4>
                    <ul className="space-y-2 text-white/90">
                      <li className="flex items-start gap-2">
                        <ChevronRight className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                        <span>Prevents competitors from registering confusingly similar marks</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                        <span>Creates valuable intellectual property assets for your company</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                        <span>Enhances credibility with customers, investors, and partners</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                        <span>Enables licensing and franchising opportunities</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed text-center italic">
                "Protecting your trademark isn't just a legal necessity—it's a strategic business investment that 
                safeguards your reputation, prevents marketplace confusion, and builds long-term brand equity. 
                Your brand is your most valuable asset; federal registration ensures it stays yours."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section id="services" className="section-padding">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center">Comprehensive Trademark Services</h2>
          <div className="w-24 h-1 bg-secondary mx-auto mb-4"></div>
          <p className="text-center text-gray-600 max-w-3xl mx-auto mb-16 text-lg">
            From initial search to final registration and beyond, we provide complete end-to-end trademark services 
            to protect your brand at every stage. Our experienced attorneys guide you through the entire USPTO process 
            with personalized attention and proven strategies that maximize your chances of successful registration.
          </p>

          <div className="space-y-8">
            {services.map((service, index) => (
              <Card key={index} className="card-hover overflow-hidden">
                <div className={`grid md:grid-cols-2 gap-8 ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`}>
                  <div className={`p-8 ${index % 2 === 1 ? 'md:col-start-2' : ''}`}>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="p-3 bg-secondary/10 rounded-lg text-secondary">
                        {service.icon}
                      </div>
                      <h3 className="text-2xl font-bold text-primary">{service.title}</h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-lg">
                      {service.description}
                    </p>
                    <Button onClick={() => setIsPopupOpen(true)} className="mt-6 btn-primary">
                      Learn More <ChevronRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                  <div className={`${index % 2 === 1 ? 'md:col-start-1 md:row-start-1' : ''}`}>
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-primary text-white">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-white text-center">Why Choose Us</h2>
          <div className="w-24 h-1 bg-secondary mx-auto mb-4"></div>
          <p className="text-center text-white/90 max-w-3xl mx-auto mb-16 text-lg">
            Experience the difference of working with dedicated trademark professionals who prioritize 
            your brand protection and business success.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <Card key={index} className="card-hover bg-white/10 backdrop-blur-sm border-white/20 p-8 text-center">
                <div className="inline-flex items-center justify-center p-4 bg-secondary rounded-full mb-6">
                  {item.icon}
                </div>
                <div className="text-4xl font-bold text-secondary mb-2">{item.stat}</div>
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-white/80 mb-4">{item.description}</p>
                <p className="text-sm text-white/70 leading-relaxed">{item.detail}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section id="process" className="section-padding">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center">Trademark Registration Process</h2>
          <div className="w-24 h-1 bg-secondary mx-auto mb-4"></div>
          <p className="text-center text-gray-600 max-w-3xl mx-auto mb-16">
            Our streamlined three-step process makes trademark registration simple, transparent, and stress-free. 
            We guide you through each stage with expert support and clear communication.
          </p>

          <div className="max-w-4xl mx-auto">
            {process.map((step, index) => (
              <div key={index} className="flex gap-6 mb-8 relative">
                {index < process.length - 1 && (
                  <div className="absolute left-[31px] top-16 bottom-0 w-0.5 bg-secondary/30"></div>
                )}
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-secondary text-primary font-bold text-xl flex items-center justify-center">
                    {step.step}
                  </div>
                </div>
                <Card className="card-hover flex-1 p-6">
                  <h3 className="text-2xl font-bold text-primary mb-3">{step.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{step.description}</p>
                </Card>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button onClick={() => setIsPopupOpen(true)} size="lg" className="btn-primary">
              Start Your Trademark Journey <ChevronRight className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <PricingSection onOpenPopup={() => setIsPopupOpen(true)} />

      {/* Industries */}
      <IndustriesSection />

      {/* Benefits */}
      <section className="section-padding bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center">Benefits of Trademark Registration</h2>
          <div className="w-24 h-1 bg-secondary mx-auto mb-4"></div>
          <p className="text-center text-gray-600 max-w-3xl mx-auto mb-16 text-lg">
            Federal trademark registration provides powerful legal protections and substantial business advantages 
            that strengthen your brand, protect your market position, and support long-term business growth and expansion.
          </p>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <Card key={index} className="card-hover p-8">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-secondary/10 rounded-lg text-secondary flex-shrink-0">
                    {benefit.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-3">{benefit.title}</h3>
                    <p className="text-gray-700 leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <Card className="mt-12 p-8 bg-primary text-white max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">Additional Benefits of Federal Registration</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-secondary mt-1 flex-shrink-0" />
                <span>Right to use ® symbol with your mark</span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-secondary mt-1 flex-shrink-0" />
                <span>Public notice of ownership claim</span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-secondary mt-1 flex-shrink-0" />
                <span>Listing in USPTO database</span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-secondary mt-1 flex-shrink-0" />
                <span>Ability to file in other countries</span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-secondary mt-1 flex-shrink-0" />
                <span>Customs protection against imports</span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-secondary mt-1 flex-shrink-0" />
                <span>Constructive notice of claim</span>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Trust Section */}
      <section className="section-padding">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.pexels.com/photos/7792803/pexels-photo-7792803.jpeg" 
                alt="Successful trademark registrations"
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-primary mb-6">Trusted by Businesses Nationwide</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                For over a decade, businesses across America have trusted us to protect their most valuable 
                asset—their brand. Our commitment to excellence, transparent processes, and client success 
                has earned us recognition as a leading trademark registration service provider.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-secondary" />
                  </div>
                  <div>
                    <div className="font-bold text-primary text-xl">1000+ Trademarks Filed</div>
                    <div className="text-gray-600">Successfully registered and protected</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-secondary" />
                  </div>
                  <div>
                    <div className="font-bold text-primary text-xl">95% Success Rate</div>
                    <div className="text-gray-600">Industry-leading approval percentage</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-secondary" />
                  </div>
                  <div>
                    <div className="font-bold text-primary text-xl">A+ Rating</div>
                    <div className="text-gray-600">Exceptional client satisfaction</div>
                  </div>
                </div>
              </div>
              <Button onClick={() => setIsPopupOpen(true)} size="lg" className="btn-primary">
                Join Our Success Stories <ChevronRight className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection />

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-primary to-primary/90 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Protect Your Brand?</h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8 leading-relaxed">
            Don't wait until it's too late. Secure your trademark today and gain the legal protection 
            your brand deserves. Our expert team is ready to guide you through the entire registration process.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Button onClick={() => setIsPopupOpen(true)} size="lg" className="btn-secondary">
              Register My Trademark Now
            </Button>
            <Button onClick={() => scrollToSection('contact')} size="lg" variant="outline" className="btn-outline-light">
              Schedule Free Consultation
            </Button>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-3xl mx-auto">
            <div>
              <div className="text-3xl font-bold text-secondary mb-2">Same Day</div>
              <div className="text-white/80">Filing Available</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-secondary mb-2">No Hidden</div>
              <div className="text-white/80">Fees or Charges</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-secondary mb-2">100%</div>
              <div className="text-white/80">Satisfaction Guarantee</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center">Get in Touch</h2>
          <div className="w-24 h-1 bg-secondary mx-auto mb-4"></div>
          <p className="text-center text-gray-600 max-w-3xl mx-auto mb-16">
            Have questions about trademark registration? Our expert team is here to help. 
            Contact us today for a free consultation.
          </p>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="card-hover p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary/10 mb-4">
                <Phone className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">Phone</h3>
              <a href="tel:+11231234567" className="text-gray-700 hover:text-secondary transition-colors">
                +1 (123) 123-4567
              </a>
            </Card>
            <Card className="card-hover p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary/10 mb-4">
                <Mail className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">Email</h3>
              <a href="mailto:info@trademarkorganizationname.com" className="text-gray-700 hover:text-secondary transition-colors">
                info@trademarkorganizationname.com
              </a>
            </Card>
            <Card className="card-hover p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary/10 mb-4">
                <MapPin className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">Address</h3>
              <p className="text-gray-700">
                7080 Hollywood Blvd Suite 700<br />
                Los Angeles, CA 90028
              </p>
            </Card>
          </div>

          <Card className="max-w-2xl mx-auto p-8">
            <h3 className="text-2xl font-bold text-primary mb-6 text-center">Send Us a Message</h3>
            <Button onClick={() => setIsPopupOpen(true)} size="lg" className="btn-primary w-full">
              Open Contact Form
            </Button>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary text-white pt-16 pb-8">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Shield className="w-8 h-8 text-secondary" />
                <span className="text-xl font-bold">Trademark Organization Name</span>
              </div>
              <p className="text-white/80 mb-4">
                Professional trademark registration services to protect your brand and intellectual property.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('about')} className="text-white/80 hover:text-secondary transition-colors">About Us</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-white/80 hover:text-secondary transition-colors">Services</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="text-white/80 hover:text-secondary transition-colors">Pricing</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="text-white/80 hover:text-secondary transition-colors">FAQ</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li className="text-white/80">Trademark Search</li>
                <li className="text-white/80">USPTO Filing</li>
                <li className="text-white/80">Brand Protection</li>
                <li className="text-white/80">Trademark Monitoring</li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">Contact Info</h4>
              <ul className="space-y-2 text-white/80">
                <li className="flex items-start gap-2">
                  <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
                  <span>7080 Hollywood Blvd Suite 700, Los Angeles, CA 90028</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-5 h-5 flex-shrink-0" />
                  <a href="tel:+11231234567" className="hover:text-secondary transition-colors">+1 (123) 123-4567</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-5 h-5 flex-shrink-0" />
                  <a href="mailto:info@trademarkorganizationname.com" className="hover:text-secondary transition-colors">info@trademarkorganizationname.com</a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 text-center text-white/60">
            <p>&copy; {new Date().getFullYear()} Trademark Organization Name. All rights reserved.</p>
            <div className="mt-4 space-x-4">
              <Link to="/privacy-policy" className="hover:text-secondary transition-colors">Privacy Policy</Link>
              <span>|</span>
              <Link to="/terms-conditions" className="hover:text-secondary transition-colors">Terms & Conditions</Link>
            </div>
          </div>
        </div>
      </footer>

      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default HomePage;
